import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserInfo } from '../helper/Context';

/**
 * AccessCodeModal - A modal popup for AI features requiring access code
 * Users can either request access, enter an existing access code, or cancel.
 * 
 * @param {Object} props
 * @param {boolean} props.show - Whether to show the modal
 * @param {function} props.onHide - Called when modal is closed/cancelled
 * @param {function} props.onAccessGranted - Called when access is successfully granted
 * @param {string} props.featureName - Name of the feature being gated (for display)
 */

// const ACCESS_CODE = 'demo'; // The access code that unlocks features
//TODO replace access code arg with API call in the future
const AccessCodeModal = ({ show, onHide, onAccessGranted, featureName = 'this feature', requiredCode = "", featureKey = "", zIndex = 1060, hideCancelButton = false, overlayAlpha = 0.85 }) => {
  const navigate = useNavigate();
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const { userInfo } = useContext(UserInfo);
  if (show && featureKey && userInfo?.permissions?.[featureKey]) {
    onAccessGranted();
  }

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setAccessCode('');
      setError('');
    }
  }, [show]);

  const handleSubmitCode = (e) => {
    if (!requiredCode) {
      return;
    }
    e.preventDefault();
    setError('');

    if (accessCode.toLowerCase().trim() === requiredCode.toLowerCase()) {
      onAccessGranted();
    } else {
      setError('Invalid access code');
      setAccessCode('');
    }
  };

  const handleRequestAccess = () => {
    onHide(); // Close the modal first
    // Navigate to contact page with pre-filled subject
    navigate('/contact?subject=AI%20Feature%20Access%20Request');
  };

  const handleCancel = () => {
    setAccessCode('');
    setError('');
    onHide();
  };

  if (!show) return null;

  return (
    <div style={{ ...styles.overlay, zIndex: zIndex, background: `rgba(10, 15, 44, ${overlayAlpha})` }}
      onClick={handleCancel}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        {/* Decorative top accent */}
        <div style={styles.topAccent} />

        {/* Icon */}
        <div style={styles.iconContainer}>
          <svg
            width="56"
            height="56"
            viewBox="0 0 24 24"
            fill="none"
            stroke="url(#accessGradientModal)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <defs>
              <linearGradient id="accessGradientModal" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#00d8ff" />
                <stop offset="100%" stopColor="#0084ff" />
              </linearGradient>
            </defs>
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        {/* Title */}
        <h2 style={styles.title}>AI Feature Access</h2>

        {/* Description */}
        <p style={styles.description}>
          <strong style={styles.featureName}>{featureName}</strong> is an experimental feature that requires {requiredCode ? "an access code" : "permission to access"}.
          Request access{requiredCode ? " or enter your code below." : " below."}
        </p>

        {/* Access Code Form */}
        {requiredCode && (
          <>
            <form onSubmit={handleSubmitCode} style={styles.form}>
              <div style={styles.inputGroup}>
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder="Enter access code"
                  style={styles.input}
                  autoFocus
                />
                <button
                  type="submit"
                  style={{
                    ...styles.submitButton,
                    opacity: !accessCode.trim() ? 0.6 : 1,
                    cursor: !accessCode.trim() ? 'not-allowed' : 'pointer',
                  }}
                  disabled={!accessCode.trim()}
                >
                  Unlock
                </button>
              </div>

              {error && <p style={styles.error}>{error}</p>}
            </form>

            {/* Divider */}
            <div style={styles.divider}>
              <span style={styles.dividerText}>or</span>
            </div>
          </>
        )}

        {/* Request Access Button */}
        <button
          onClick={handleRequestAccess}
          style={styles.requestButton}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(0, 216, 255, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
          }}
        >
          Request Access
        </button>

        {/* Cancel Button */}
        {!hideCancelButton && (
          <button
            onClick={handleCancel}
            style={styles.cancelButton}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
            }}
          >
            Cancel
          </button>
        )}
        {/* Info text */}
        <p style={styles.infoText}>
          AI features are currently in limited beta.
        </p>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  card: {
    position: 'relative',
    background: 'linear-gradient(135deg, rgba(30, 35, 60, 0.98), rgba(20, 25, 50, 0.98))',
    borderRadius: '24px',
    border: '1px solid rgba(0, 216, 255, 0.3)',
    boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(0, 216, 255, 0.1)',
    padding: '48px 40px',
    maxWidth: '440px',
    width: '100%',
    textAlign: 'center',
    overflow: 'hidden',
  },
  topAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #00d8ff, #0084ff, #00d8ff)',
  },
  iconContainer: {
    marginBottom: '20px',
  },
  title: {
    color: '#ffffff',
    fontSize: '26px',
    fontWeight: '700',
    marginBottom: '16px',
    fontFamily: "'Helvetica', sans-serif",
  },
  description: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '15px',
    lineHeight: '1.6',
    marginBottom: '28px',
  },
  featureName: {
    color: '#00d8ff',
  },
  form: {
    marginBottom: '20px',
  },
  inputGroup: {
    display: 'flex',
    gap: '10px',
  },
  input: {
    flex: 1,
    padding: '14px 18px',
    fontSize: '16px',
    border: '1px solid rgba(0, 216, 255, 0.3)',
    borderRadius: '10px',
    background: 'rgba(255, 255, 255, 0.05)',
    color: '#ffffff',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  submitButton: {
    padding: '14px 24px',
    fontSize: '16px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #00d8ff 0%, #0084ff 100%)',
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s, opacity 0.2s',
    boxShadow: '0 4px 14px rgba(0, 136, 255, 0.4)',
  },
  error: {
    color: '#ff6b6b',
    fontSize: '14px',
    marginTop: '10px',
    marginBottom: '0',
    textAlign: 'left',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '20px 0',
  },
  dividerText: {
    flex: 1,
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: '14px',
    position: 'relative',
  },
  requestButton: {
    width: '100%',
    padding: '14px 24px',
    fontSize: '16px',
    fontWeight: '600',
    border: '1px solid rgba(0, 216, 255, 0.4)',
    borderRadius: '10px',
    background: 'transparent',
    color: '#00d8ff',
    cursor: 'pointer',
    transition: 'background 0.2s',
    marginBottom: '12px',
  },
  cancelButton: {
    width: '100%',
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '500',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '10px',
    background: 'transparent',
    color: 'rgba(255, 255, 255, 0.6)',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  infoText: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: '13px',
    marginTop: '20px',
    marginBottom: 0,
  },
};

export default AccessCodeModal;

